import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SplashScreen } from '@capacitor/splash-screen';
import { ensureNativeOAuthListener } from '@/features/auth/googleAuth';
import { isNativeApp, setNativeSystemBarsStyle, syncNativeSystemBarsStyle } from '@/lib/nativeApp';

const MIN_SPLASH_MS = 1400;
const BOOT_ID = 'native-boot';

function removeHtmlBootSplash() {
  document.getElementById(BOOT_ID)?.remove();
}

function isAppEntryPath(pathname: string) {
  return (
    pathname === '/connexion' ||
    pathname === '/inscription' ||
    pathname.startsWith('/auth/') ||
    pathname.startsWith('/app') ||
    pathname.startsWith('/admin')
  );
}

/**
 * Bootstrap APK : garde le logo (splash natif + HTML) jusqu’à la page de connexion,
 * puis masque proprement sans flash de la landing marketing.
 */
export function NativeBootstrap() {
  const location = useLocation();
  const startedAtRef = useRef(Date.now());
  const hiddenRef = useRef(false);
  const [barsReady, setBarsReady] = useState(false);

  useEffect(() => {
    if (!isNativeApp()) {
      removeHtmlBootSplash();
      return;
    }

    ensureNativeOAuthListener();
    // Overlay de boot navy : icônes claires, puis icônes sombres (UI claire).
    setNativeSystemBarsStyle('dark');
  }, []);

  useEffect(() => {
    if (!isNativeApp() || hiddenRef.current) return;
    if (!isAppEntryPath(location.pathname)) return;

    let cancelled = false;

    async function hideSplashWhenReady() {
      const elapsed = Date.now() - startedAtRef.current;
      const wait = Math.max(0, MIN_SPLASH_MS - elapsed);
      if (wait > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, wait));
      }
      if (cancelled || hiddenRef.current) return;

      hiddenRef.current = true;
      // Masquer d’abord le splash natif (pastille carrée) pendant que #native-boot
      // (logo normal) reste au-dessus — sinon un flash du 2e logo apparaît.
      try {
        await SplashScreen.hide({ fadeOutDuration: 0 });
      } catch {
        // Déjà masqué
      }
      removeHtmlBootSplash();
      setBarsReady(true);
      syncNativeSystemBarsStyle(location.pathname);
    }

    void hideSplashWhenReady();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!isNativeApp() || !barsReady) return;
    syncNativeSystemBarsStyle(location.pathname);
  }, [barsReady, location.pathname]);

  return null;
}
